import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useTranslation } from 'react-i18next';
import './CodeGeneratorPage.css';

/**
 * 消息类型接口
 */
interface Message {
  type: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

/**
 * 流式API响应数据接口  
 */
interface StreamResponse {
  category: string;
  content: string;
}

/**
 * 预览接口响应接口
 */
interface PreviewResponse {
  filename: string;
  full_path: string;
}

/**
 * 代码生成和预览页面组件
 * 左侧是对话输入框，右侧显示生成结果并支持预览功能
 */
const CodeGeneratorPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // 对话相关状态
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // 结果展示相关状态
  const [generatedCode, setGeneratedCode] = useState<string>('');
  const [isPreviewMode, setIsPreviewMode] = useState<boolean>(false);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [isGeneratingPreview, setIsGeneratingPreview] = useState<boolean>(false);
  
  // refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const previewIframeRef = useRef<HTMLIFrameElement>(null);

  /**
   * 添加和移除特殊的布局类
   */
  useEffect(() => {
    // 为body和html添加特殊类，确保正确的布局
    document.body.classList.add('code-generator-page');
    document.documentElement.classList.add('code-generator-page');
    
    // 组件卸载时移除类
    return () => {
      document.body.classList.remove('code-generator-page');
      document.documentElement.classList.remove('code-generator-page');
    };
  }, []);

  /**
   * 自动滚动到聊天底部
   */
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  /**
   * 处理表单提交 - 发送消息并调用流式API
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputText.trim() || isLoading) return;

    const userMessage: Message = {
      type: 'user',
      content: inputText,
      timestamp: new Date().toISOString()
    };

    // 添加用户消息
    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsLoading(true);
    setError(null);
    setGeneratedCode('');
    setIsPreviewMode(false);
    setPreviewUrl('');

    try {
      await callStreamAPI(inputText);
    } catch (err) {
      setError('调用API失败，请重试');
      console.error('API调用失败:', err);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 调用流式API生成代码
   */
  const callStreamAPI = async (userInput: string) => {
    const token = localStorage.getItem('token') || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJ0ZXN0QHFxLmNvbSIsImV4cCI6MTc0OTc4NDg0Mn0.KJpucFNeMeJgB-I1uWB7ZtAuDf4odUC7DLjHHiqZUBc';
    
    try {
      const response = await fetch('https://ceshi.lightark.cc/api/v1/stream/test', {
        method: 'POST',
        headers: {
          'authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          app_name: "我的应用",
          app_description: userInput,
          language: "zh"
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('无法获取响应流');
      }

      const decoder = new TextDecoder();
      let accumulatedCode = '';
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n').filter(line => line.trim());
        
        for (const line of lines) {
          try {
            const data: StreamResponse = JSON.parse(line);
            if (data.category === 'result') {
              accumulatedCode += data.content;
              setGeneratedCode(accumulatedCode);
            }
          } catch (parseError) {
            console.warn('解析流数据失败:', parseError);
          }
        }
      }

      // 添加助手消息
      const assistantMessage: Message = {
        type: 'assistant',
        content: '代码生成完成！您可以点击预览按钮查看效果。',
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, assistantMessage]);

    } catch (error) {
      console.error('流式API调用失败:', error);
      throw error;
    }
  };

  /**
   * 处理预览按钮点击
   */
  const handlePreview = async () => {
    if (!generatedCode.trim()) {
      setError('没有可预览的代码');
      return;
    }

    setIsGeneratingPreview(true);
    setError(null);

    try {
      const token = localStorage.getItem('token') || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJ0ZXN0QHFxLmNvbSIsImV4cCI6MTc0OTc4NDg0Mn0.KJpucFNeMeJgB-I1uWB7ZtAuDf4odUC7DLjHHiqZUBc';

      const response = await fetch('https://ceshi.lightark.cc/api/v1/apps/generate_temp_html', {
        method: 'POST',
        headers: {
          'authorization': `Bearer ${token}`,
          'Content-Type': 'application/xml'
        },
        body: generatedCode
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: PreviewResponse = await response.json();
      const previewUrl = `https://ceshi.lightark.cc/web/${result.filename}`;
      
      setPreviewUrl(previewUrl);
      setIsPreviewMode(true);

    } catch (error) {
      setError('生成预览失败，请重试');
      console.error('预览生成失败:', error);
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  /**
   * 切换到编辑模式
   */
  const handleBackToEdit = () => {
    setIsPreviewMode(false);
    setPreviewUrl('');
  };

  /**
   * 清空对话
   */
  const handleClearChat = () => {
    setMessages([]);
    setGeneratedCode('');
    setIsPreviewMode(false);
    setPreviewUrl('');
    setError(null);
  };

  return (
    <Layout hideFooter={true}>
      <div className="code-generator-container">
        {/* 左侧对话面板 */}
        <div className="chat-panel">
          <div className="chat-header">
            <h2 className="chat-title">代码生成器</h2>
            <button 
              className="clear-button"
              onClick={handleClearChat}
              disabled={isLoading}
            >
              清空对话
            </button>
          </div>

          <div className="chat-messages">
            {messages.map((message, index) => (
              <div key={index} className={`message ${message.type}`}>
                {message.type === 'user' ? (
                  <div className="message-content">
                    <div className="message-text">{message.content}</div>
                    <div className="message-time">
                      {new Date(message.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                ) : (
                  <div className="message-content">
                    <div className="assistant-message-container">
                      <div className="assistant-message-header">
                        <div className="coding-icon">
                          &lt;/&gt;
                        </div>
                        <span className="assistant-label">代码生成器</span>
                      </div>
                      <div className="assistant-message-body">
                        <div className="message-text">{message.content}</div>
                      </div>
                    </div>
                    <div className="message-time">
                      {new Date(message.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                )}
              </div>
            ))}
            
            {isLoading && (
              <div className="message assistant">
                <div className="message-content">
                  <div className="assistant-message-container">
                    <div className="assistant-message-header">
                      <div className="coding-icon">
                        &lt;/&gt;
                      </div>
                      <span className="assistant-label">代码生成器</span>
                    </div>
                    <div className="assistant-message-body">
                      <div className="loading-indicator">
                        <span>正在生成代码...</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          <form className="chat-input-form" onSubmit={handleSubmit}>
            <div className="input-container">
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="请描述您想要生成的代码..."
                disabled={isLoading}
                rows={3}
                className="chat-input"
              />
              <button
                type="submit"
                disabled={isLoading || !inputText.trim()}
                className="send-button"
              >
                {isLoading ? '生成中...' : '发送'}
              </button>
            </div>
          </form>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
        </div>

        {/* 右侧结果面板 */}
        <div className="result-panel">
          <div className="result-header">
            <h3 className="result-title">
              {isPreviewMode ? '预览效果' : '生成结果'}
            </h3>
            <div className="result-actions">
              {!isPreviewMode && generatedCode && (
                <button
                  className="preview-button"
                  onClick={handlePreview}
                  disabled={isGeneratingPreview}
                >
                  {isGeneratingPreview ? '生成中...' : '预览'}
                </button>
              )}
              {isPreviewMode && (
                <button
                  className="edit-button"
                  onClick={handleBackToEdit}
                >
                  返回编辑
                </button>
              )}
            </div>
          </div>

          <div className="result-content">
            {!isPreviewMode ? (
              <div className="code-display">
                {generatedCode ? (
                  <pre className="code-block">
                    <code>{generatedCode}</code>
                  </pre>
                ) : (
                  <div className="empty-state">
                    <p>开始对话生成您的代码</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="preview-container">
                {previewUrl ? (
                  <iframe
                    ref={previewIframeRef}
                    src={previewUrl}
                    className="preview-iframe"
                    title="代码预览"
                    sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals allow-downloads"
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      display: 'block'
                    }}
                  />
                ) : (
                  <div className="preview-loading">
                    <p>正在加载预览...</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CodeGeneratorPage; 